import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch {
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(res.data);
    return res.data;
  }, []);

  const signup = useCallback(async (email, password, name) => {
    const res = await axios.post(`${API}/auth/signup`, { email, password, name }, { withCredentials: true });
    setUser(res.data);
    return res.data;
  }, []);

  const googleAuth = useCallback(async (sessionId) => {
    const res = await axios.post(`${API}/auth/google`, { session_id: sessionId }, { withCredentials: true });
    setUser(res.data);
    return res.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setUser(null);
  }, []);

  const syncProgress = useCallback(async (wordId) => {
    if (!user) return;
    try {
      await axios.post(`${API}/progress/learn`, { word_id: wordId }, { withCredentials: true });
      setUser((prev) => prev ? { ...prev, learned_words: [...new Set([...prev.learned_words, wordId])] } : prev);
    } catch (e) {
      console.error("Failed to sync progress", e);
    }
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, googleAuth, logout, checkAuth, syncProgress }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
